import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Spacer } from "@amzn/stencil-react-components/layout";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Select } from "@amzn/stencil-react-components/select";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import React, { FC, useCallback, useState } from "react";
import { getString } from "../../uistringlabels/ui-string-utils";
import {
  CANCEL_STRINGS_PATH,
  CancellationReason,
  CancellationState,
  OrderDetails,
} from "../../../services/orders/cancellation.types";

interface CancelOrderModalProps {
  onClose: () => void;
  onConfirm: (reason: CancellationReason) => void;
  orderDetails: OrderDetails;
  cancellationState: CancellationState;
}

const MODAL_STRINGS = `${CANCEL_STRINGS_PATH}.modal`;

const REASON_LABELS: Record<CancellationReason, string> = {
  [CancellationReason.NO_LONGER_NEEDED]: getString(`${MODAL_STRINGS}.reasons.NO_LONGER_NEEDED`),
  [CancellationReason.ORDERED_BY_MISTAKE]: getString(`${MODAL_STRINGS}.reasons.ORDERED_BY_MISTAKE`),
  [CancellationReason.SWITCHING_TO_DIFFERENT_ASSET]: getString(
    `${MODAL_STRINGS}.reasons.SWITCHING_TO_DIFFERENT_ASSET`
  ),
  [CancellationReason.OTHER]: getString(`${MODAL_STRINGS}.reasons.OTHER`),
};

export const CancelOrderModal: FC<CancelOrderModalProps> = ({ onClose, onConfirm, orderDetails, cancellationState }) => {
  const [selectedReason, setSelectedReason] = useState<CancellationReason | undefined>(undefined);
  const { isLoading, error } = cancellationState;

  const reasonOptions = Object.values(CancellationReason).map((reason) => REASON_LABELS[reason]);

  const handleReasonChange = useCallback((selectedValue: string) => {
    const reason = Object.entries(REASON_LABELS).find(([, label]) => label === selectedValue)?.[0] as
      | CancellationReason
      | undefined;
    setSelectedReason(reason);
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedReason) {
      onConfirm(selectedReason);
    }
  }, [onConfirm, selectedReason]);

  return (
    <ModalContent
      titleText={getString(`${MODAL_STRINGS}.title`)}
      maxWidth="500px"
      buttons={[
        <Button
          key="keep-order"
          variant={ButtonVariant.Secondary}
          onClick={onClose}
          disabled={isLoading}
          data-testid="keep-order-button"
        >
          {getString(`${MODAL_STRINGS}.keepOrder`)}
        </Button>,
        <Button
          key="cancel-order"
          variant={ButtonVariant.Primary}
          onClick={handleConfirm}
          disabled={isLoading || !selectedReason}
          data-testid="confirm-cancel-button"
          style={!isLoading && selectedReason ? { backgroundColor: "#b33225", borderColor: "#b33225", color: "white" } : undefined}
        >
          {isLoading ? (
            <>
              {getString(`${MODAL_STRINGS}.cancelling`)}
              <Spinner size={SpinnerSize.Small} />
            </>
          ) : (
            getString(`${MODAL_STRINGS}.confirm`)
          )}
        </Button>,
      ]}
    >
      <Col>
        <Text>{getString(`${MODAL_STRINGS}.intro`)}</Text>
        <Spacer height={12} />

        <div style={{ backgroundColor: "#f5f5f5", padding: "12px 16px", borderRadius: "4px" }}>
          <div style={{ display: "flex", gap: "8px" }}>
            <Text fontWeight="bold">{getString(`${MODAL_STRINGS}.assetNameLabel`)}</Text>
            <Text color="neutral70">{orderDetails.assetName}</Text>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Text fontWeight="bold">{getString(`${MODAL_STRINGS}.orderNumberLabel`)}</Text>
            <Text color="neutral70">{orderDetails.orderReference}</Text>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Text fontWeight="bold">{getString(`${MODAL_STRINGS}.requestDateLabel`)}</Text>
            <Text color="neutral70">{orderDetails.requestDate}</Text>
          </div>
        </div>

        <Spacer height={12} />

        {error && (
          <Text color="red70" data-testid="error-message">{error}</Text>
        )}

        <div style={{ backgroundColor: "#FFF8E1", borderLeft: "4px solid #F5A623", padding: "12px 16px" }}>
          <Text>{getString(`${MODAL_STRINGS}.warning`)}</Text>
        </div>

        <Spacer height={16} />

        <Label htmlFor="cancellation-reason">{getString(`${MODAL_STRINGS}.reasonLabel`)}</Label>
        <Spacer height={8} />
        <Select
          id="cancellation-reason"
          data-testid="cancellation-reason-select"
          options={reasonOptions}
          placeholder={getString(`${MODAL_STRINGS}.reasonPlaceholder`)}
          onChange={handleReasonChange}
          value={selectedReason ? REASON_LABELS[selectedReason] : ""}
          disabled={isLoading}
          listPositioningStrategy="fixed"
        />
      </Col>
    </ModalContent>
  );
};
